import React from 'react';
import axios from '../../../../axios-config.js';

const InputField = ({ title, value, onChange, placeholder, type = 'text', hint }) => {
  return (
    <label className="CreateCompanyInput modernField">
      <div className="input-title">{title}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint ? <div className="input-hint">{hint}</div> : null}
    </label>
  );
};

const BooleanInput = ({ title, value, onChange, hint }) => {
  return (
    <label className="CompanyEditorCheckboxField" title={hint || ''}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      <span>{title}</span>
    </label>
  );
};

class ProjectForm extends React.Component {
  constructor(props) {
    super(props);
    const currentProject = {
      public: false,
      project_name: '',
      latitude: 0.0,
      longitude: 0.0,
      project_slug: '',
      project_id: '',
      legacy_url: '',
      company_id: '',
      flipLabel: false,
    };

    if (props.company) {
      currentProject.company_id = props.company.id;
    }

    if (props.project) {
      Object.assign(currentProject, props.project);
    }

    this.state = {
      name: currentProject.project_name,
      visiblePublic: currentProject.public,
      latitude: currentProject.latitude,
      longitude: currentProject.longitude,
      slug: currentProject.project_slug,
      existingID: currentProject.project_id,
      legacyURL: currentProject.legacy_url || '',
      flipLabel: currentProject.flip_map_label,
      companyID: currentProject.company_id,
      deleting: false,
      saving: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  handleSubmit() {
    const state = this.state;
    const latitude = parseFloat(state.latitude);
    const longitude = parseFloat(state.longitude);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      window.alert('Latitude and longitude must be valid numbers.');
      return;
    }

    if (!state.name || !String(state.name).trim()) {
      window.alert('Project name cannot be blank.');
      return;
    }

    if (!state.slug || !String(state.slug).trim()) {
      window.alert('Project slug cannot be blank.');
      return;
    }

    this.setState({ saving: true });

    if (state.existingID.length > 0) {
      axios.post(`/project/${state.existingID}`, {
        name: state.name,
        visiblePublic: state.visiblePublic,
        latitude,
        longitude,
        slug: state.slug,
        flipLabel: state.flipLabel,
        legacyURL: state.legacyURL,
      })
      .then(() => {
        this.props.back();
      })
      .catch((error) => {
        const message = error?.response?.data?.message || 'Failed to update project.';
        window.alert(message);
      })
      .finally(() => this.setState({ saving: false }));
      return;
    }

    axios.post('/project', {
      name: state.name,
      visiblePublic: state.visiblePublic,
      latitude,
      longitude,
      slug: state.slug,
      companyID: state.companyID,
      flipLabel: state.flipLabel,
      legacyURL: state.legacyURL,
    })
    .then(() => {
      this.props.back();
    })
    .catch((error) => {
      const message = error?.response?.data?.message || 'Failed to create project.';
      window.alert(message);
    })
    .finally(() => this.setState({ saving: false }));
  }

  handleDelete() {
    if (!this.state.existingID || !this.props.isSuperuser) {
      return;
    }

    const firstConfirm = window.confirm(`Delete project "${this.state.name}"? This cannot be undone.`);
    if (!firstConfirm) {
      return;
    }

    const typed = window.prompt('Type DELETE to confirm project deletion.');
    if (typed !== 'DELETE') {
      return;
    }

    this.setState({ deleting: true });
    axios.delete(`/project/${this.state.existingID}`, { data: { confirm: 'DELETE' } })
      .then(() => {
        this.props.back();
      })
      .catch((error) => {
        const message = error?.response?.data?.message || error?.response?.data?.error?.message || 'Failed to delete project.';
        window.alert(message);
        this.setState({ deleting: false });
      });
  }

  render() {
    const state = this.state;
    const isEditing = state.existingID.length > 0;
    const showLegacyURL = !!String(this.props.project?.legacy_url || '').trim();
    const legacyURLHint = showLegacyURL
      ? 'Only use this for legacy Inventum3D projects. Public map and directory links will open this URL instead of the current project viewer, and the project can appear publicly without a master scene.'
      : null;

    return (
      <div className="CompanyEditorModal">
        <div className="CompanyEditorHeader">
          <div className="CompanyEditorTitle">{isEditing ? 'Edit Project' : 'Create Project'}</div>
          <div className="CompanyEditorSubtitle">
            {isEditing
              ? 'Update project visibility, geolocation, URL settings, and editor behavior.'
              : 'Create a new project and configure map visibility and URL metadata.'}
          </div>
        </div>

        <div className="CompanyEditorSection">
          <div className="CompanyEditorFormGrid">
            <InputField title="Project Name" placeholder="ABC Project" value={state.name} onChange={(name) => this.setState({ name })} />
            <InputField title="Project Slug" placeholder="abc-project" value={state.slug} onChange={(slug) => this.setState({ slug })} />

            <div className="CompanyEditorTwoColumnRow">
              <InputField title="Latitude" placeholder="-27.4705" value={state.latitude} onChange={(latitude) => this.setState({ latitude })} />
              <InputField title="Longitude" placeholder="153.0260" value={state.longitude} onChange={(longitude) => this.setState({ longitude })} />
            </div>

            {showLegacyURL ? (
              <InputField
                title="Legacy URL (optional)"
                placeholder="https://mapability.com.au/review/..."
                value={state.legacyURL}
                onChange={(legacyURL) => this.setState({ legacyURL })}
                hint={legacyURLHint}
              />
            ) : null}

            <div className="CompanyEditorToggleRow">
              <BooleanInput
                title="Show on public map"
                value={state.visiblePublic}
                onChange={(visiblePublic) => this.setState({ visiblePublic })}
                hint="Public projects appear on the public map when a master scene is available."
              />
              <BooleanInput
                title={`Label direction: ${state.flipLabel ? 'Left' : 'Right'}`}
                value={state.flipLabel}
                onChange={(flipLabel) => this.setState({ flipLabel })}
              />
            </div>
          </div>

          <div className="CompanyEditorFormHint">
            Projects must have a master scene and public visibility enabled to appear to non-company users.
          </div>

          <div className="CompanyEditorFormActions">
            <button type="button" className="CompanyEditorSecondaryButton" onClick={this.props.back}>Cancel</button>
            <button type="button" className="CompanyEditorPrimaryButton" onClick={this.handleSubmit} disabled={state.saving}>
              {state.saving ? 'Saving…' : (isEditing ? 'Save Project' : 'Create Project')}
            </button>
          </div>
        </div>

        {isEditing && this.props.isSuperuser ? (
          <div className="CompanyEditorSection">
            <div className="CompanyEditorSectionTitle">Danger Zone</div>
            <button type="button" className="RemoveUserButton PermissionActionButton remove DangerZoneButton" onClick={this.handleDelete}>
              {state.deleting ? 'Deleting…' : 'Delete Project (Superuser)'}
            </button>
          </div>
        ) : null}
      </div>
    );
  }
}

export default ProjectForm;
